import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { House, Users, Calendar, ChartBar, Article, Briefcase, EnvelopeSimple, SignOut } from "@phosphor-icons/react";

const NAV = [
  { to: "/admin", label: "Overview", icon: House, end: true },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/appointments", label: "Appointments", icon: Calendar },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/blog", label: "Insights CMS", icon: Article },
  { to: "/admin/careers", label: "Careers", icon: Briefcase },
  { to: "/admin/newsletter", label: "Newsletter", icon: EnvelopeSimple },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-midnight text-white flex">
      <aside className="w-64 bg-midnight border-r border-white/10 p-6 hidden md:flex flex-col">
        <div className="mb-10 bg-white/95 rounded-lg p-2 inline-flex items-center gap-2">
          <img src="/logo.webp" alt="Manigriv" className="h-9 w-auto" />
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g,"-")}`} className={({isActive}) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive ? "bg-white/10 text-gold" : "text-white/70 hover:bg-white/5"}`}>
              <n.icon size={18}/> {n.label}
            </NavLink>
          ))}
        </nav>
        <button data-testid="admin-logout" onClick={async ()=>{await logout(); nav("/");}} className="mt-6 pt-6 border-t border-white/10 flex items-center gap-2 text-sm text-white/70 hover:text-gold"><SignOut size={16}/> Logout</button>
      </aside>
      <main className="flex-1 bg-[#0e2b1e] overflow-auto"><Outlet/></main>
    </div>
  );
}

export function AdminOverview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/analytics/summary").then(r=>setS(r.data)); }, []);
  if (!s) return <div className="p-12">Loading…</div>;
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold mb-3">Analytics</div>
      <h1 className="font-display text-4xl text-white mb-10">Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <AdminStat k="Total Leads" v={s.total_leads} sub={`${s.new_leads} new`}/>
        <AdminStat k="Appointments" v={s.appointments} sub={`${s.pending_appointments} pending`}/>
        <AdminStat k="Clients" v={s.clients}/>
        <AdminStat k="Newsletter" v={s.newsletter_subscribers}/>
      </div>
      <div className="bg-dark-card rounded-2xl p-6 border border-white/10">
        <div className="overline text-gold mb-4">Leads by source</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {s.leads_by_source.map(x => (
            <div key={x.source} className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="text-xs text-white/60 capitalize">{x.source}</div>
              <div className="font-display text-3xl text-gold mt-1">{x.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function AdminStat({ k, v, sub }) {
  return <div className="p-6 bg-dark-card rounded-2xl border border-white/10">
    <div className="overline text-white/50 mb-2">{k}</div>
    <div className="font-display text-4xl text-gold">{v}</div>
    {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
  </div>;
}

export function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const load = () => api.get("/leads").then(r=>setLeads(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => { await api.patch(`/leads/${id}`, patch); load(); toast.success("Updated"); };
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold mb-3">Inbox</div>
      <h1 className="font-display text-4xl text-white mb-10">Leads</h1>
      <div className="bg-dark-card rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm text-white/85">
          <thead className="bg-white/5 text-left text-xs uppercase text-white/60"><tr><th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Source</th><th className="p-4">Message</th><th className="p-4">Status</th></tr></thead>
          <tbody>
            {leads.map(l => (
              <tr key={l.id} className="border-t border-white/10">
                <td className="p-4"><div className="font-semibold">{l.name}</div><div className="text-xs text-white/50">{new Date(l.created_at).toLocaleDateString()}</div></td>
                <td className="p-4"><div>{l.email}</div><div className="text-xs text-white/50">{l.phone}</div></td>
                <td className="p-4 capitalize">{l.source}</td>
                <td className="p-4 max-w-xs truncate">{l.message || l.subject || "—"}</td>
                <td className="p-4">
                  <select value={l.status} onChange={(e)=>update(l.id,{status:e.target.value})} data-testid={`lead-status-${l.id}`} className="bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-xs">
                    <option value="new">new</option><option value="contacted">contacted</option><option value="converted">converted</option><option value="dropped">dropped</option>
                  </select>
                </td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-white/50">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminAppointments() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/appointments").then(r=>setItems(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, status) => { await api.patch(`/appointments/${id}`, { status }); load(); toast.success("Updated"); };
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold mb-3">Bookings</div>
      <h1 className="font-display text-4xl text-white mb-10">Appointments</h1>
      <div className="space-y-3">
        {items.map(a => (
          <div key={a.id} className="p-5 bg-dark-card rounded-xl border border-white/10 flex flex-wrap gap-4 items-center justify-between">
            <div>
              <div className="font-display text-lg">{a.name} · {a.service}</div>
              <div className="text-sm text-white/60">{a.email} · {a.phone}</div>
              <div className="text-xs text-gold mt-1">{new Date(a.slot).toLocaleString()}</div>
            </div>
            <div className="flex gap-2">
              {["pending","confirmed","completed","cancelled"].map(s => (
                <button key={s} onClick={()=>update(a.id,s)} data-testid={`appt-${a.id}-${s}`} className={`px-3 py-1.5 rounded-full text-xs border ${a.status===s ? "bg-gold border-gold text-midnight" : "border-white/15 text-white/70 hover:border-gold"}`}>{s}</button>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/50">No appointments yet.</p>}
      </div>
    </div>
  );
}

export function AdminClients() {
  const [clients, setClients] = useState([]);
  const [pfClient, setPfClient] = useState(null);
  const [pf, setPf] = useState([]);
  const [docs, setDocs] = useState([]);
  const [pfForm, setPfForm] = useState({ asset_type: "Mutual Fund", name: "", invested: 0, current_value: 0 });
  const [docForm, setDocForm] = useState({ name: "", category: "KYC", url: "", notes: "" });
  const loadClients = () => api.get("/clients").then(r=>setClients(r.data));
  useEffect(() => { loadClients(); }, []);
  const openClient = async (c) => { setPfClient(c); const [p,d] = await Promise.all([api.get("/portfolio",{params:{client_id:c.id}}), api.get("/documents",{params:{client_id:c.id}})]); setPf(p.data); setDocs(d.data); };
  const addPf = async (e) => { e.preventDefault(); try { await api.post("/portfolio",{...pfForm, client_id: pfClient.id, invested: Number(pfForm.invested), current_value: Number(pfForm.current_value)}); const p=await api.get("/portfolio",{params:{client_id:pfClient.id}}); setPf(p.data); toast.success("Added"); setPfForm({asset_type:"Mutual Fund",name:"",invested:0,current_value:0}); } catch(err){toast.error(formatErr(err));} };
  const addDoc = async (e) => { e.preventDefault(); try { await api.post("/documents",{...docForm, client_id: pfClient.id}); const d=await api.get("/documents",{params:{client_id:pfClient.id}}); setDocs(d.data); toast.success("Added"); setDocForm({name:"",category:"KYC",url:"",notes:""}); } catch(err){toast.error(formatErr(err));} };
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold mb-3">Directory</div>
      <h1 className="font-display text-4xl text-white mb-10">Clients</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-dark-card rounded-2xl border border-white/10 overflow-hidden">
          {clients.map(c => (
            <button key={c.id} onClick={()=>openClient(c)} data-testid={`client-row-${c.id}`} className={`w-full text-left p-4 border-b border-white/10 last:border-0 hover:bg-white/5 transition-colors ${pfClient?.id===c.id?"bg-white/10":""}`}>
              <div className="font-semibold text-white">{c.name}</div>
              <div className="text-xs text-white/60">{c.email}</div>
            </button>
          ))}
          {clients.length === 0 && <p className="p-6 text-white/50 text-sm">No clients yet.</p>}
        </div>
        <div className="lg:col-span-2 space-y-6">
          {pfClient ? <>
            <div className="bg-dark-card rounded-2xl border border-white/10 p-6">
              <h3 className="font-display text-xl mb-4">Portfolio · {pfClient.name}</h3>
              <div className="space-y-2 mb-4">
                {pf.map(e => (<div key={e.id} className="flex justify-between p-3 bg-white/5 rounded-lg text-sm"><div><div className="font-semibold">{e.name}</div><div className="text-xs text-white/60">{e.asset_type}</div></div><div className="text-right"><div>₹{e.current_value.toLocaleString("en-IN")}</div><div className="text-xs text-white/60">Inv ₹{e.invested.toLocaleString("en-IN")}</div></div></div>))}
                {pf.length===0 && <p className="text-white/50 text-sm">Empty.</p>}
              </div>
              <form onSubmit={addPf} className="grid grid-cols-2 gap-2 text-xs">
                <input placeholder="Asset name" required value={pfForm.name} onChange={(e)=>setPfForm({...pfForm,name:e.target.value})} className="p-2 bg-white/5 border border-white/10 rounded"/>
                <select value={pfForm.asset_type} onChange={(e)=>setPfForm({...pfForm,asset_type:e.target.value})} className="p-2 bg-white/5 border border-white/10 rounded">{["Equity","Mutual Fund","Bond","Gold","Cash","Other"].map(x=><option key={x}>{x}</option>)}</select>
                <input type="number" placeholder="Invested" required value={pfForm.invested} onChange={(e)=>setPfForm({...pfForm,invested:e.target.value})} className="p-2 bg-white/5 border border-white/10 rounded"/>
                <input type="number" placeholder="Current value" required value={pfForm.current_value} onChange={(e)=>setPfForm({...pfForm,current_value:e.target.value})} className="p-2 bg-white/5 border border-white/10 rounded"/>
                <button data-testid="admin-add-pf" className="col-span-2 btn-gold text-xs">Add holding</button>
              </form>
            </div>
            <div className="bg-dark-card rounded-2xl border border-white/10 p-6">
              <h3 className="font-display text-xl mb-4">Documents</h3>
              <div className="space-y-2 mb-4">
                {docs.map(d => (<div key={d.id} className="flex justify-between p-3 bg-white/5 rounded-lg text-sm"><div><div className="font-semibold">{d.name}</div><div className="text-xs text-white/60">{d.category}</div></div><a href={d.url} target="_blank" rel="noreferrer" className="text-gold text-xs">Open</a></div>))}
                {docs.length===0 && <p className="text-white/50 text-sm">Empty.</p>}
              </div>
              <form onSubmit={addDoc} className="grid grid-cols-2 gap-2 text-xs">
                <input placeholder="Document name" required value={docForm.name} onChange={(e)=>setDocForm({...docForm,name:e.target.value})} className="p-2 bg-white/5 border border-white/10 rounded"/>
                <select value={docForm.category} onChange={(e)=>setDocForm({...docForm,category:e.target.value})} className="p-2 bg-white/5 border border-white/10 rounded">{["KYC","Statement","Agreement","Report","Other"].map(x=><option key={x}>{x}</option>)}</select>
                <input placeholder="URL (drive/dropbox link)" required value={docForm.url} onChange={(e)=>setDocForm({...docForm,url:e.target.value})} className="col-span-2 p-2 bg-white/5 border border-white/10 rounded"/>
                <button data-testid="admin-add-doc" className="col-span-2 btn-gold text-xs">Add document</button>
              </form>
            </div>
          </> : <div className="bg-dark-card rounded-2xl border border-white/10 p-8 text-white/50">Select a client to view portfolio and documents.</div>}
        </div>
      </div>
    </div>
  );
}

export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", content: "", category: "Markets", cover_image: "", published: true });
  const load = () => api.get("/blog", { params: { published_only: false } }).then(r=>setPosts(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => { e.preventDefault(); try { await api.post("/blog", form); toast.success("Published"); setForm({ title:"",slug:"",excerpt:"",content:"",category:"Markets",cover_image:"",published:true }); load(); } catch(err){toast.error(formatErr(err));} };
  const del = async (id) => { if(!confirm("Delete?")) return; await api.delete(`/blog/${id}`); load(); };
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold mb-3">CMS</div>
      <h1 className="font-display text-4xl text-white mb-10">Insights</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={save} className="bg-dark-card rounded-2xl border border-white/10 p-6 space-y-3">
          <h3 className="font-display text-xl mb-2">New post</h3>
          <input required placeholder="Title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")})} data-testid="blog-title" className="w-full p-3 bg-white/5 border border-white/10 rounded text-sm"/>
          <input required placeholder="slug" value={form.slug} onChange={(e)=>setForm({...form,slug:e.target.value})} data-testid="blog-slug" className="w-full p-3 bg-white/5 border border-white/10 rounded text-sm"/>
          <input required placeholder="Category" value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} data-testid="blog-category-input" className="w-full p-3 bg-white/5 border border-white/10 rounded text-sm"/>
          <input placeholder="Cover image URL" value={form.cover_image} onChange={(e)=>setForm({...form,cover_image:e.target.value})} className="w-full p-3 bg-white/5 border border-white/10 rounded text-sm"/>
          <textarea required rows={2} placeholder="Excerpt" value={form.excerpt} onChange={(e)=>setForm({...form,excerpt:e.target.value})} data-testid="blog-excerpt" className="w-full p-3 bg-white/5 border border-white/10 rounded text-sm resize-none"/>
          <textarea required rows={8} placeholder="Content HTML" value={form.content} onChange={(e)=>setForm({...form,content:e.target.value})} data-testid="blog-content" className="w-full p-3 bg-white/5 border border-white/10 rounded text-sm resize-none font-mono"/>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e)=>setForm({...form,published:e.target.checked})}/> Published</label>
          <button data-testid="blog-save" className="btn-gold text-sm w-full">Publish</button>
        </form>
        <div className="space-y-3">
          {posts.map(p => (
            <div key={p.id} className="bg-dark-card rounded-xl border border-white/10 p-5 flex justify-between items-start gap-4">
              <div><div className="overline text-gold text-[10px] mb-1">{p.category} · {p.published?"Live":"Draft"}</div><div className="font-display text-lg">{p.title}</div><div className="text-xs text-white/60 mt-1">{p.slug}</div></div>
              <button onClick={()=>del(p.id)} data-testid={`blog-delete-${p.id}`} className="text-xs text-red-400 hover:text-red-300">Delete</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdminCareers() {
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState({ title: "", department: "Advisory", location: "Delhi NCR", type: "Full-time", description: "", active: true });
  const load = async () => { const [j,a] = await Promise.all([api.get("/careers/jobs",{params:{active_only:false}}), api.get("/careers/applications")]); setJobs(j.data); setApps(a.data); };
  useEffect(() => { load(); }, []);
  const save = async (e) => { e.preventDefault(); try { await api.post("/careers/jobs", form); toast.success("Created"); setForm({...form, title:"", description:""}); load(); } catch(err){toast.error(formatErr(err));} };
  const del = async (id) => { if(!confirm("Delete?")) return; await api.delete(`/careers/jobs/${id}`); load(); };
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold mb-3">Talent</div>
      <h1 className="font-display text-4xl text-white mb-10">Careers</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <form onSubmit={save} className="bg-dark-card rounded-2xl border border-white/10 p-6 space-y-3 mb-6">
            <h3 className="font-display text-xl">New role</h3>
            <input required placeholder="Title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} data-testid="job-title" className="w-full p-3 bg-white/5 border border-white/10 rounded text-sm"/>
            <div className="grid grid-cols-3 gap-2">
              <input placeholder="Department" value={form.department} onChange={(e)=>setForm({...form,department:e.target.value})} className="p-3 bg-white/5 border border-white/10 rounded text-sm"/>
              <input placeholder="Location" value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} className="p-3 bg-white/5 border border-white/10 rounded text-sm"/>
              <input placeholder="Type" value={form.type} onChange={(e)=>setForm({...form,type:e.target.value})} className="p-3 bg-white/5 border border-white/10 rounded text-sm"/>
            </div>
            <textarea required rows={4} placeholder="Description" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} data-testid="job-desc" className="w-full p-3 bg-white/5 border border-white/10 rounded text-sm resize-none"/>
            <button data-testid="job-save" className="btn-gold text-sm w-full">Create job</button>
          </form>
          <div className="space-y-3">
            {jobs.map(j => (
              <div key={j.id} className="bg-dark-card rounded-xl border border-white/10 p-5 flex justify-between items-start"><div><div className="font-display text-lg">{j.title}</div><div className="text-xs text-white/60">{j.department} · {j.location} · {j.type}</div></div><button onClick={()=>del(j.id)} className="text-xs text-red-400">Delete</button></div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-display text-xl mb-4 text-white">Applications</h3>
          <div className="space-y-3">
            {apps.map(a => (
              <div key={a.id} className="bg-dark-card rounded-xl border border-white/10 p-5">
                <div className="flex justify-between"><div className="font-semibold">{a.name}</div><span className="overline text-[10px] text-gold">{a.status}</span></div>
                <div className="text-xs text-white/60 mt-1">{a.email} · {a.phone} · {a.experience_years||"—"} yrs</div>
                <p className="text-xs text-white/70 mt-2 line-clamp-3">{a.cover_letter}</p>
              </div>
            ))}
            {apps.length===0 && <p className="text-white/50 text-sm">No applications yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminNewsletter() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/newsletter").then(r=>setSubs(r.data)); }, []);
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold mb-3">Subscribers</div>
      <h1 className="font-display text-4xl text-white mb-10">Newsletter</h1>
      <div className="bg-dark-card rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm text-white/85">
          <thead className="bg-white/5 text-left text-xs uppercase text-white/60"><tr><th className="p-4">Email</th><th className="p-4">Subscribed</th></tr></thead>
          <tbody>
            {subs.map(s => <tr key={s.id} className="border-t border-white/10"><td className="p-4">{s.email}</td><td className="p-4">{new Date(s.created_at).toLocaleDateString()}</td></tr>)}
            {subs.length===0 && <tr><td colSpan="2" className="p-8 text-center text-white/50">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
